import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Send, Plus, QrCode, Loader2, RefreshCw, PowerOff, Smartphone,
  Mic, Square, Paperclip, ImageIcon, FileText, UserPlus, CheckCircle2, MessageCircle
} from "lucide-react";
import { toast } from "sonner";

function isImage(ct) { return ct && ct.startsWith("image/"); }
function isAudio(ct) { return ct && ct.startsWith("audio/"); }
function humanTime(iso) {
  try { return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

function BlobMedia({ fileId, contentType, name }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true; let u;
    api.get(`/files/${fileId}`, { responseType: "blob" })
      .then((r) => { u = URL.createObjectURL(r.data); if (alive) setUrl(u); })
      .catch(() => {});
    return () => { alive = false; if (u) URL.revokeObjectURL(u); };
  }, [fileId]);

  if (!url) return <div className="text-xs text-muted-foreground">Carregando anexo…</div>;
  if (isImage(contentType)) return <img src={url} alt={name || ""} className="mt-1 max-h-56 rounded-md" />;
  if (isAudio(contentType)) return <audio controls src={url} className="mt-1 w-full" />;
  return (
    <a href={url} target="_blank" rel="noreferrer"
       className="mt-1 inline-flex items-center gap-2 rounded-md border border-border px-2 py-1 text-xs hover:bg-accent">
      <FileText className="h-3.5 w-3.5" /> {name || "arquivo"}
    </a>
  );
}

function useRecorder() {
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const intervalRef = useRef(null);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/ogg";
      const mr = new MediaRecorder(stream, { mimeType: mime });
      mediaRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.start();
      setRecording(true); setElapsed(0);
      intervalRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    } catch (e) {
      toast.error("Não foi possível acessar o microfone");
      throw e;
    }
  };

  const stop = () =>
    new Promise((resolve, reject) => {
      const mr = mediaRef.current;
      if (!mr) return reject(new Error("no recorder"));
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType });
        mr.stream.getTracks().forEach((t) => t.stop());
        clearInterval(intervalRef.current);
        setRecording(false); setElapsed(0);
        resolve(blob);
      };
      mr.stop();
    });

  const cancel = () => {
    const mr = mediaRef.current;
    if (mr && mr.state !== "inactive") mr.stop();
    if (mr) mr.stream.getTracks().forEach((t) => t.stop());
    clearInterval(intervalRef.current);
    chunksRef.current = [];
    setRecording(false); setElapsed(0);
  };

  return { recording, elapsed, start, stop, cancel };
}

export default function Chat() {
  const { user } = useAuth();
  const navigate = useNavigate();

  /* ------------- Internal chat ------------- */
  const [convs, setConvs] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState("");
  const [newConvOpen, setNewConvOpen] = useState(false);
  const [newConvName, setNewConvName] = useState("");
  const [uploadingChat, setUploadingChat] = useState(false);
  const chatFileRef = useRef(null);
  const chatBottomRef = useRef(null);
  const chatRec = useRecorder();

  /* ------------- WhatsApp ------------- */
  const [wa, setWa] = useState({ connected: false, offline: false });
  const [qr, setQr] = useState(null);
  const [loadingQR, setLoadingQR] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [activeContact, setActiveContact] = useState(null);
  const [waMessages, setWaMessages] = useState([]);
  const [waText, setWaText] = useState("");
  const [uploadingWa, setUploadingWa] = useState(false);
  const waFileRef = useRef(null);
  const waBottomRef = useRef(null);
  const waRec = useRecorder();

  /* ---------- Loaders ---------- */
  const loadConvs = async () => {
    try { const r = await api.get("/chat/conversations"); setConvs(r.data); } catch { /* silent */ }
  };
  const loadMessages = async (id) => {
    try { const r = await api.get("/chat/messages", { params: { conversation_id: id } }); setMessages(r.data); }
    catch { /* silent */ }
  };
  const loadWA = async () => {
    try { const r = await api.get("/whatsapp/status"); setWa(r.data); } catch { /* silent */ }
  };
  const loadContacts = async () => {
    try { const r = await api.get("/whatsapp/contacts"); setContacts(r.data); } catch { /* silent */ }
  };
  const loadContactMessages = async (phone) => {
    try { const r = await api.get("/whatsapp/messages", { params: { phone } }); setWaMessages(r.data); }
    catch { /* silent */ }
  };

  useEffect(() => { loadConvs(); loadWA(); loadContacts(); }, []);
  useEffect(() => { if (activeConv) loadMessages(activeConv.id); }, [activeConv]);
  useEffect(() => { if (activeContact) loadContactMessages(activeContact.phone); }, [activeContact]);
  useEffect(() => { chatBottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);
  useEffect(() => { waBottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [waMessages]);

  // Poll WA status + contacts every 15s so QR/queue refresh
  useEffect(() => {
    const id = setInterval(() => { loadWA(); loadContacts(); if (activeContact) loadContactMessages(activeContact.phone); }, 15000);
    return () => clearInterval(id);
  }, [activeContact]);

  /* ---------- Internal chat actions ---------- */
  const sendInternal = async (e) => {
    e?.preventDefault?.();
    if ((!message.trim()) || !activeConv) return;
    try {
      await api.post("/chat/messages", { conversation_id: activeConv.id, message });
      setMessage(""); loadMessages(activeConv.id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const uploadChatFile = async (file) => {
    if (!activeConv) return;
    setUploadingChat(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const up = await api.post("/chat/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/chat/messages", {
        conversation_id: activeConv.id,
        message: "",
        attachment_file_id: up.data.file_id,
      });
      loadMessages(activeConv.id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setUploadingChat(false); }
  };

  const stopAndSendAudio = async () => {
    try {
      const blob = await chatRec.stop();
      const f = new File([blob], `audio-${Date.now()}.webm`, { type: blob.type });
      await uploadChatFile(f);
    } catch (e) { toast.error(e.message); }
  };

  const createConv = async (e) => {
    e.preventDefault();
    try {
      await api.post("/chat/conversations", null, { params: { name: newConvName } });
      setNewConvOpen(false); setNewConvName(""); loadConvs();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  /* ---------- WhatsApp actions ---------- */
  const genQR = async () => {
    setLoadingQR(true);
    try {
      const r = await api.post("/whatsapp/qrcode");
      if (r.data.connected) { setQr(null); loadWA(); toast.success("WhatsApp já conectado"); }
      else { setQr(r.data); toast.success("QR gerado. Escaneie no WhatsApp."); }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setLoadingQR(false); }
  };
  const disconnect = async () => {
    try { await api.post("/whatsapp/disconnect"); setQr(null); loadWA(); toast.success("Desconectado"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const sendWaText = async (e) => {
    e?.preventDefault?.();
    if (!waText.trim() || !activeContact) return;
    try {
      await api.post("/whatsapp/send-text", { to: activeContact.phone, text: waText });
      setWaText(""); loadContactMessages(activeContact.phone); loadContacts();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const uploadWaFile = async (file, caption) => {
    if (!activeContact) return;
    setUploadingWa(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const qs = new URLSearchParams({ to: activeContact.phone });
      if (caption) qs.set("caption", caption);
      await api.post(`/whatsapp/send-media?${qs.toString()}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      loadContactMessages(activeContact.phone); loadContacts();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setUploadingWa(false); }
  };

  const stopAndSendWaAudio = async () => {
    try {
      const blob = await waRec.stop();
      const f = new File([blob], `audio-${Date.now()}.webm`, { type: blob.type });
      await uploadWaFile(f);
    } catch (e) { toast.error(e.message); }
  };

  const markResolved = async (contact, resolved) => {
    try {
      await api.patch("/whatsapp/contacts", { phone: contact.phone, resolved });
      loadContacts();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const goRegister = (contact) => {
    const params = new URLSearchParams({ phone: contact.phone });
    if (contact.push_name) params.set("name", contact.push_name);
    // Copy link to clipboard for admin to send to client if wanted, and navigate too
    navigate(`/cadastro?${params.toString()}`);
  };

  return (
    <div className="space-y-8" data-testid="chat-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Comunicação</div>
          <h1 className="font-heading text-4xl font-semibold tracking-tight">Chat & WhatsApp</h1>
          <p className="mt-2 text-sm text-muted-foreground">Chat interno com áudio e mídia, e atendimento multi-atendente via WhatsApp real.</p>
        </div>
        {wa.pending_contacts > 0 && (
          <Badge className="bg-amber-600 text-white" data-testid="wa-pending-badge">
            {wa.pending_contacts} contato(s) pendente(s)
          </Badge>
        )}
      </div>

      <Tabs defaultValue="chat">
        <TabsList data-testid="chat-tabs">
          <TabsTrigger value="chat" data-testid="tab-chat">Chat Interno</TabsTrigger>
          <TabsTrigger value="whatsapp" data-testid="tab-whatsapp">
            WhatsApp {wa.connected && <span className="ml-2 h-2 w-2 rounded-full bg-emerald-500" />}
          </TabsTrigger>
        </TabsList>

        {/* ============ INTERNAL CHAT ============ */}
        <TabsContent value="chat" className="mt-6">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[280px_1fr]">
            <Card className="h-[70vh] overflow-hidden">
              <CardHeader className="flex flex-row items-center justify-between border-b border-border py-4">
                <CardTitle className="font-heading text-sm uppercase tracking-[0.15em]">Conversas</CardTitle>
                <Dialog open={newConvOpen} onOpenChange={setNewConvOpen}>
                  <DialogTrigger asChild>
                    <Button size="icon" variant="ghost" data-testid="new-conv-btn"><Plus className="h-4 w-4" /></Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Nova conversa</DialogTitle></DialogHeader>
                    <form onSubmit={createConv} className="space-y-4">
                      <div><Label>Nome</Label>
                        <Input required value={newConvName} onChange={(e) => setNewConvName(e.target.value)} data-testid="conv-name-input" /></div>
                      <DialogFooter><Button type="submit" data-testid="conv-save-btn">Criar</Button></DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent className="p-0">
                <div className="max-h-[calc(70vh-56px)] overflow-y-auto">
                  {convs.length === 0 && (<p className="p-4 text-xs text-muted-foreground">Nenhuma conversa.</p>)}
                  {convs.map((c) => (
                    <button key={c.id} onClick={() => setActiveConv(c)} data-testid={`conv-${c.id}`}
                      className={`flex w-full flex-col items-start gap-1 border-b border-border px-4 py-3 text-left hover:bg-accent ${
                        activeConv?.id === c.id ? "bg-accent" : ""}`}>
                      <div className="text-sm font-medium">{c.name}</div>
                      <div className="line-clamp-1 text-xs text-muted-foreground">{c.last_message || "Sem mensagens"}</div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="flex h-[70vh] flex-col">
              {!activeConv ? (
                <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">Selecione uma conversa.</div>
              ) : (
                <>
                  <CardHeader className="border-b border-border py-4">
                    <CardTitle className="font-heading text-lg">{activeConv.name}</CardTitle>
                  </CardHeader>
                  <CardContent className="flex-1 space-y-3 overflow-y-auto p-6">
                    {messages.length === 0 && <p className="text-center text-xs text-muted-foreground py-8">Sem mensagens.</p>}
                    {messages.map((m) => {
                      const mine = m.author_id === user.id;
                      return (
                        <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                          <div className={`max-w-[75%] rounded-md border px-3 py-2 text-sm ${
                            mine ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card"}`}>
                            {!mine && <div className="mb-1 text-[10px] uppercase tracking-wider opacity-70">{m.author_name}</div>}
                            {m.message && <p className="whitespace-pre-wrap">{m.message}</p>}
                            {m.attachment && (
                              <BlobMedia fileId={m.attachment.file_id}
                                contentType={m.attachment.content_type} name={m.attachment.filename} />
                            )}
                            <div className="mt-1 text-right text-[10px] opacity-70 font-mono">{humanTime(m.created_at)}</div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={chatBottomRef} />
                  </CardContent>
                  <form onSubmit={sendInternal} className="flex items-center gap-2 border-t border-border p-4">
                    <input ref={chatFileRef} type="file" hidden onChange={(e) => {
                      const f = e.target.files?.[0]; if (f) uploadChatFile(f); e.target.value = "";
                    }} />
                    <Button type="button" size="icon" variant="ghost"
                      onClick={() => chatFileRef.current?.click()} disabled={uploadingChat}
                      data-testid="chat-attach-btn">
                      {uploadingChat ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
                    </Button>
                    {chatRec.recording ? (
                      <>
                        <Button type="button" size="icon" variant="destructive"
                          onClick={stopAndSendAudio} data-testid="chat-stop-audio-btn">
                          <Square className="h-4 w-4" />
                        </Button>
                        <div className="rounded-md border border-red-500/30 bg-red-500/10 px-2 py-1 text-xs font-mono text-red-600">
                          ● {String(Math.floor(chatRec.elapsed/60)).padStart(2,"0")}:{String(chatRec.elapsed%60).padStart(2,"0")}
                        </div>
                      </>
                    ) : (
                      <Button type="button" size="icon" variant="ghost"
                        onClick={chatRec.start} data-testid="chat-record-btn">
                        <Mic className="h-4 w-4" />
                      </Button>
                    )}
                    <Input placeholder="Mensagem..." value={message} onChange={(e) => setMessage(e.target.value)} data-testid="chat-message-input" />
                    <Button type="submit" data-testid="chat-send-btn"><Send className="h-4 w-4" /></Button>
                  </form>
                </>
              )}
            </Card>
          </div>
        </TabsContent>

        {/* ============ WHATSAPP ============ */}
        <TabsContent value="whatsapp" className="mt-6">
          {/* Status */}
          <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
            <Card>
              <CardContent className="flex items-center gap-4 p-5">
                <div className="rounded-md border border-border p-3"><Smartphone className="h-5 w-5" /></div>
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Status</div>
                  <div className="mt-1 flex items-center gap-2">
                    {wa.offline ? (
                      <Badge variant="outline" className="border-red-500/40 text-red-600">Serviço offline</Badge>
                    ) : wa.connected ? (
                      <Badge className="bg-emerald-600 text-white">Conectado</Badge>
                    ) : (
                      <Badge variant="outline">Desconectado</Badge>
                    )}
                    {wa.phone_number && <span className="font-mono text-xs">+{wa.phone_number}</span>}
                  </div>
                </div>
                {user.role === "admin" && (
                  wa.connected
                    ? <Button variant="outline" size="sm" onClick={disconnect} data-testid="wa-disconnect-btn"><PowerOff className="mr-1 h-3.5 w-3.5" /> Desconectar</Button>
                    : <Button size="sm" onClick={genQR} disabled={loadingQR} data-testid="wa-gen-qr-btn">
                        {loadingQR ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <QrCode className="mr-1 h-3.5 w-3.5" />} QR Code
                      </Button>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Contatos pendentes</div>
                <div className="mt-1 font-heading text-3xl font-semibold text-amber-600">{wa.pending_contacts || 0}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Total de conversas</div>
                <div className="mt-1 font-heading text-3xl font-semibold">{contacts.length}</div>
              </CardContent>
            </Card>
          </div>

          {qr && !wa.connected && (
            <Card className="mb-6">
              <CardContent className="flex flex-col items-center gap-4 p-6">
                <img src={qr.qr_image} alt="QR" className="h-64 w-64 rounded-md border border-border bg-white p-3" data-testid="wa-qr-image" />
                <p className="text-center text-xs text-muted-foreground">
                  Abra o WhatsApp &gt; Aparelhos conectados &gt; Conectar um aparelho e escaneie.
                </p>
                <Button variant="outline" size="sm" onClick={genQR} data-testid="wa-refresh-qr-btn">
                  <RefreshCw className="mr-2 h-3.5 w-3.5" /> Novo QR
                </Button>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[320px_1fr]">
            {/* Contacts queue */}
            <Card className="h-[70vh] overflow-hidden">
              <CardHeader className="border-b border-border py-4">
                <CardTitle className="font-heading text-sm uppercase tracking-[0.15em]">Fila de contatos</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="max-h-[calc(70vh-56px)] overflow-y-auto">
                  {contacts.length === 0 && (
                    <p className="p-4 text-xs text-muted-foreground">
                      Nenhum contato ainda. Assim que alguém mandar mensagem no seu WhatsApp, aparece aqui.
                    </p>
                  )}
                  {contacts.map((c) => (
                    <button key={c.phone} onClick={() => setActiveContact(c)}
                      data-testid={`wa-contact-${c.phone}`}
                      className={`flex w-full flex-col items-start gap-1 border-b border-border px-4 py-3 text-left hover:bg-accent ${
                        activeContact?.phone === c.phone ? "bg-accent" : ""}`}>
                      <div className="flex w-full items-center justify-between gap-2">
                        <span className="truncate text-sm font-medium">{c.push_name || `+${c.phone}`}</span>
                        {!c.resolved && <span className="h-2 w-2 rounded-full bg-amber-500" />}
                      </div>
                      <div className="text-xs text-muted-foreground font-mono">+{c.phone}</div>
                      <div className="line-clamp-1 text-xs text-muted-foreground">
                        {c.last_direction === "in" ? "↓ " : "↑ "}{c.last_message || "—"}
                      </div>
                      {c.linked_user_name && (
                        <Badge variant="secondary" className="mt-1 text-[10px]">Vinculado: {c.linked_user_name}</Badge>
                      )}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Conversation */}
            <Card className="flex h-[70vh] flex-col">
              {!activeContact ? (
                <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
                  <MessageCircle className="mr-2 h-4 w-4" /> Selecione um contato para responder.
                </div>
              ) : (
                <>
                  <CardHeader className="border-b border-border py-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <CardTitle className="font-heading text-lg">
                          {activeContact.push_name || `+${activeContact.phone}`}
                        </CardTitle>
                        <div className="text-xs text-muted-foreground font-mono">+{activeContact.phone}</div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {!activeContact.linked_user_id && (
                          <Button size="sm" variant="outline" onClick={() => goRegister(activeContact)}
                            data-testid={`wa-register-${activeContact.phone}`}>
                            <UserPlus className="mr-1 h-3.5 w-3.5" /> Cadastrar cliente
                          </Button>
                        )}
                        {activeContact.resolved ? (
                          <Button size="sm" variant="ghost" onClick={() => markResolved(activeContact, false)}
                            data-testid={`wa-reopen-${activeContact.phone}`}>Reabrir</Button>
                        ) : (
                          <Button size="sm" variant="ghost" onClick={() => markResolved(activeContact, true)}
                            data-testid={`wa-resolve-${activeContact.phone}`}>
                            <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Marcar resolvido
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="flex-1 space-y-3 overflow-y-auto p-6">
                    {waMessages.length === 0 && <p className="text-center text-xs text-muted-foreground py-8">Sem mensagens.</p>}
                    {waMessages.map((m) => {
                      const mine = m.direction === "out";
                      return (
                        <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`wa-msg-${m.id}`}>
                          <div className={`max-w-[75%] rounded-md border px-3 py-2 text-sm ${
                            mine ? "border-primary bg-primary text-primary-foreground"
                                 : "border-emerald-500/30 bg-emerald-500/5"}`}>
                            {m.message && <p className="whitespace-pre-wrap">{m.message}</p>}
                            {m.attachment && (
                              <BlobMedia fileId={m.attachment.file_id}
                                contentType={m.attachment.content_type} name={m.attachment.filename} />
                            )}
                            <div className="mt-1 text-right text-[10px] opacity-70 font-mono">{humanTime(m.created_at)}</div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={waBottomRef} />
                  </CardContent>
                  <form onSubmit={sendWaText} className="flex items-center gap-2 border-t border-border p-4">
                    <input ref={waFileRef} type="file" hidden
                      accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.mp3,.ogg,.wav,.mp4,.mov,.doc,.docx"
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadWaFile(f); e.target.value = ""; }} />
                    <Button type="button" size="icon" variant="ghost"
                      onClick={() => waFileRef.current?.click()} disabled={uploadingWa || !wa.connected}
                      data-testid="wa-attach-btn">
                      {uploadingWa ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
                    </Button>
                    {waRec.recording ? (
                      <>
                        <Button type="button" size="icon" variant="destructive"
                          onClick={stopAndSendWaAudio} data-testid="wa-stop-audio-btn">
                          <Square className="h-4 w-4" />
                        </Button>
                        <div className="rounded-md border border-red-500/30 bg-red-500/10 px-2 py-1 text-xs font-mono text-red-600">
                          ● {String(Math.floor(waRec.elapsed/60)).padStart(2,"0")}:{String(waRec.elapsed%60).padStart(2,"0")}
                        </div>
                      </>
                    ) : (
                      <Button type="button" size="icon" variant="ghost"
                        onClick={waRec.start} disabled={!wa.connected}
                        data-testid="wa-record-btn">
                        <Mic className="h-4 w-4" />
                      </Button>
                    )}
                    <Input placeholder={wa.connected ? "Mensagem para o WhatsApp..." : "WhatsApp desconectado"}
                      value={waText} onChange={(e) => setWaText(e.target.value)}
                      disabled={!wa.connected} data-testid="wa-text-input" />
                    <Button type="submit" disabled={!wa.connected} data-testid="wa-send-btn"><Send className="h-4 w-4" /></Button>
                  </form>
                </>
              )}
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
